import { z } from 'zod';
import { uuidSchema } from './common';

/**
 * Phase 8 — moderation, content review and account archival vocabularies.
 *
 * Every status here is a TEXT column validated by Zod, not a postgres enum, and
 * that is deliberate: our migrator runs all pending files in ONE transaction,
 * and postgres forbids USING an enum value in the transaction that added it. A
 * text column plus a const array can grow a state in a single migration; an
 * enum needs two. `feedback.status` and `courses.status` set the precedent.
 */

/** What a member can report. */
export const CONTENT_REPORT_TARGETS = ['course', 'review', 'question', 'announcement'] as const;
export type ContentReportTarget = (typeof CONTENT_REPORT_TARGETS)[number];

export const CONTENT_REPORT_REASONS = [
  'spam',
  'inappropriate',
  'copyright',
  'misleading',
  'other',
] as const;
export type ContentReportReason = (typeof CONTENT_REPORT_REASONS)[number];

export const CONTENT_REPORT_REASON_LABELS: Record<ContentReportReason, string> = {
  spam: 'Spam or advertising',
  inappropriate: 'Offensive or inappropriate',
  copyright: 'Copyright or stolen content',
  misleading: 'Misleading or inaccurate',
  other: 'Something else',
};

export const CONTENT_REPORT_STATUSES = ['open', 'resolved', 'dismissed'] as const;
export type ContentReportStatus = (typeof CONTENT_REPORT_STATUSES)[number];

export const createContentReportSchema = z.object({
  targetKind: z.enum(CONTENT_REPORT_TARGETS),
  targetId: uuidSchema,
  reason: z.enum(CONTENT_REPORT_REASONS),
  details: z.string().trim().max(1000).optional(),
});
export type CreateContentReportInput = z.infer<typeof createContentReportSchema>;

export const resolveContentReportSchema = z.object({
  note: z.string().trim().max(500).optional(),
});

export const hideReportTargetSchema = z.object({
  hidden: z.boolean(),
});

/** Course CONTENT review state (distinct from the product's listing approval). */
export const CONTENT_REVIEW_STATUSES = ['clear', 'pending', 'rejected'] as const;
export type ContentReviewStatus = (typeof CONTENT_REVIEW_STATUSES)[number];

/** What the tenant does when playable content changes after approval. */
export const CONTENT_REREVIEW_MODES = ['flag', 'delist', 'hold'] as const;
export type ContentRereviewMode = (typeof CONTENT_REREVIEW_MODES)[number];

/**
 * Tier 1 gates review; tier 2 is recorded so a reviewer sees the whole picture
 * but never flips the course on its own.
 *
 * Phase 10 adds tier 3: the EDITORIAL surface (titles, prices, drip, ordering,
 * tests, translations). Tier 3 never gates on its own either — it gates only
 * when `lms_content_rereview_scope = 'all'`, which is the client's "review even
 * minor changes" setting. Splitting it from tier 2 keeps the Phase-8 promise
 * that attachments and captions stay non-gating under BOTH scopes.
 */
export const CONTENT_CHANGE_KINDS = [
  'lesson_source',
  'video_finalize',
  'video_clear',
  'lesson_create',
  'lesson_delete',
  'chapter_delete',
  'attachment_add',
  'attachment_delete',
  'caption_add',
  'caption_delete',
  'test_question_create',
  'test_question_update',
  'test_question_delete',
  'course_meta',
  'lesson_meta',
  'lesson_body',
  'chapter_create',
  'chapter_update',
  'chapter_reorder',
  'lesson_reorder',
  'test_create',
  'test_update',
  'test_delete',
  'translation_link',
  'translation_unlink',
  'certificate_template_assign',
  'promo_video',
] as const;
export type ContentChangeKind = (typeof CONTENT_CHANGE_KINDS)[number];

export const CONTENT_CHANGE_TIER: Record<ContentChangeKind, 1 | 2 | 3> = {
  lesson_source: 1,
  video_finalize: 1,
  video_clear: 1,
  lesson_create: 1,
  lesson_delete: 1,
  chapter_delete: 1,
  attachment_add: 2,
  attachment_delete: 2,
  caption_add: 2,
  caption_delete: 2,
  test_question_create: 2,
  test_question_update: 2,
  test_question_delete: 2,
  course_meta: 3,
  lesson_meta: 3,
  lesson_body: 3,
  chapter_create: 3,
  chapter_update: 3,
  chapter_reorder: 3,
  lesson_reorder: 3,
  test_create: 3,
  test_update: 3,
  test_delete: 3,
  translation_link: 3,
  translation_unlink: 3,
  certificate_template_assign: 3,
  promo_video: 3,
};

/**
 * The kinds `lms_content_rereview = 'hold'` STAGES instead of writing live.
 *
 * Exactly the changes that replace what an enrolled learner plays in ONE
 * lesson: a new upload, a swapped embed/source family, a cleared video, an
 * edited article body. Each is fully described by the lesson's source triple
 * (`source_type`, `embed_url`/`video_key`/`body`), so one shadow row per lesson
 * can hold it and one UPDATE can apply it.
 *
 * Everything else stays flag-shaped ON PURPOSE. A lesson added or deleted, a
 * chapter deleted, a reordering, a retimed drip, an edited test: holding those
 * means staging the SHAPE of the curriculum, and a learner sitting inside a
 * course whose lesson list is half-held has no coherent thing to be shown.
 */
export const HOLDABLE_CHANGE_KINDS = [
  'lesson_source',
  'video_finalize',
  'video_clear',
  'lesson_body',
] as const satisfies readonly ContentChangeKind[];
export type HoldableChangeKind = (typeof HOLDABLE_CHANGE_KINDS)[number];

export function isHoldableChangeKind(kind: ContentChangeKind): kind is HoldableChangeKind {
  return (HOLDABLE_CHANGE_KINDS as readonly string[]).includes(kind);
}

export const CONTENT_CHANGE_LABELS: Record<ContentChangeKind, string> = {
  lesson_source: 'Lesson source changed',
  video_finalize: 'New video uploaded',
  video_clear: 'Video removed',
  lesson_create: 'Lesson added',
  lesson_delete: 'Lesson deleted',
  chapter_delete: 'Chapter deleted',
  attachment_add: 'Attachment added',
  attachment_delete: 'Attachment removed',
  caption_add: 'Captions added',
  caption_delete: 'Captions removed',
  test_question_create: 'Test question added',
  test_question_update: 'Test question edited',
  test_question_delete: 'Test question deleted',
  course_meta: 'Course details edited',
  lesson_meta: 'Lesson details edited',
  lesson_body: 'Article text edited',
  chapter_create: 'Chapter added',
  chapter_update: 'Chapter renamed',
  chapter_reorder: 'Chapters reordered',
  lesson_reorder: 'Lessons reordered',
  test_create: 'Test added',
  test_update: 'Test settings changed',
  test_delete: 'Test deleted',
  translation_link: 'Translation linked',
  translation_unlink: 'Translation unlinked',
  certificate_template_assign: 'Certificate template changed',
  promo_video: 'Promo video changed',
};

/**
 * How much of a creator's editing re-opens review on an approved, published
 * course. `content` is the Phase-8 behaviour (tier 1 only: the playable content
 * a buyer was actually sold). `all` also gates tier 3, so a title, a price, a
 * drip date or a reordered chapter goes back through a human.
 *
 * Tier 2 gates under NEITHER: an attachment or a caption file is additive, and
 * a queue that fires on every subtitle upload is a queue nobody reads.
 */
export const CONTENT_REREVIEW_SCOPES = ['content', 'all'] as const;
export type ContentRereviewScope = (typeof CONTENT_REREVIEW_SCOPES)[number];

export const adminContentReviewSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  reason: z.string().trim().max(500).optional(),
});
export type AdminContentReviewInput = z.infer<typeof adminContentReviewSchema>;

/** Account archival (the GDPR erasure door). */
export const ARCHIVE_REQUEST_STATUSES = [
  'requested',
  'cancelled',
  'rejected',
  'completed',
] as const;
export type ArchiveRequestStatus = (typeof ARCHIVE_REQUEST_STATUSES)[number];

export const createArchiveRequestSchema = z.object({
  password: z.string().min(1),
  reason: z.string().trim().max(1000).optional(),
});

export const archiveDecisionSchema = z.object({
  note: z.string().trim().max(500).optional(),
});
